<template>
  <section class="section section--hero">
    <div class="section__container">
      <div class="section__body">
        <div>
          <div class="section__name h6">
            {{ $t('hero.section-name') }}
          </div>
          <h1 class="pt-3">
            {{ fixCzechTypography(userGameDetail?.game?.header) }}
          </h1>
        </div>
        <p class="whitespace-pre-wrap">
          {{ userGameDetail?.game?.description }}
        </p>
          </div>
      <div class="flex flex-col-reverse md:flex-col w-full relative mt-8 md:mt-0">
        <DefaultRaffleDrum
          :type="userGameState"
          :countdown="userGameStateCountdown"
          :prize="activeDrawTotalPrize"
          :psc="activeDrawWinningPsc"
          :next-chance="nextChance"
          :draw-date="drawDatetime"
          :is-loading="userGamesDetailStore.isWinCheckLoading"
          :error-message="winCheckErrorMessage"
          @open:psc-modal="openPscModal"
          @open:winning-modal="openWinningModal"
          @check-win="checkWin"
        />
        <div v-if="showTodayWin" class="flex mb-12 md:mt-12 md:mb-0 justify-center">
          <div class="relative text-center pt-2 md:pt-0 self-center">
            <div class="h6">
              {{ userGameDetail?.game?.prizeDescription }}
            </div>
            <div class="h1 heading--glow">
              {{ currency.toCurrency(activeDrawTotalPrize) }}
            </div>
            <div class="hidden md:block absolute -top-2 -right-28 -rotate-[30deg]">
              <DefaultStickerCTA/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <DefaultModalsPscModal
      v-if="pscModalOpen"
      :isOpen="pscModalOpen"
      :is-loading="userGamesDetailStore.isGameUserLoading"
      :step="pscModalStep"
      :psc="userGamesDetailStore.userGameDetail.user?.psc"
      :age-consent="userGamesDetailStore.userGameDetail.user?.hasAgeConsent"
      :error-message="pscErrorMessage"
      @close="closePscModal"
      @submit="submitPsc"
    />
    <DefaultModalsWinningsModal
      v-if="winningsModalOpen"
      :isOpen="winningsModalOpen"
      :is-loading="userGamesDetailStore.isGameUserLoading"
      :psc="userGamesDetailStore.userGameDetail.user?.psc"
      :name="userGamesDetailStore.userGameDetail.user?.name"
      :surname="userGamesDetailStore.userGameDetail.user?.surname"
      :step="winningModalStep"
      :error-message="winPickupErrorMessage"
      @close="closeWinningModal"
      @submit="submitWin"
      @goBack="goBackWin"
    />
    <DefaultModalsRulesConsentModal
      v-if="rulesConsentModalOpen"
      :isOpen="rulesConsentModalOpen"
      :is-loading="rulesConsentLoading"
      :error-message="rulesConsentErrorMessage"
      @close="closeRulesConsentModal"
      @confirm="confirmRulesConsent"
    />
  </section>
</template>

<script setup lang="ts">
import {useUserGamesDetailStore} from "~/stores/useUserGameDetailStore";
import {useUserGameStateHelper} from "~/composables/useUserGameStateHelper";
import {PscModalStep} from "~/libs/constants/PscModalStep";
import {ref} from "vue";
import {useI18n} from "vue-i18n";
import UserGameState from "~/libs/constants/UserGameState";
import {WinningModalStep} from "~/libs/constants/WinningModalStep";
import {pushToDataLayer} from "~/composables/useAnalytics";
import {useTypography} from "~/composables/useCzechTextBreakingLines";

const { fixCzechTypography } = useTypography();
const config = useRuntimeConfig()
const userGamesDetailStore = useUserGamesDetailStore()
const userGameStateHelper = useUserGameStateHelper()
const currency = useCurrency()
const logger = useFrontendLogger()
const {t} = useI18n()

const closedDrawReloadIntervalMs = Number(config.public.closedDrawReloadIntervalSeconds || 30) * 1000

const userGameStateCountdown = ref<Date|undefined>()
const userGameState = ref<UserGameState|undefined>()

const pscModalOpen = ref<boolean>(false)
const winningsModalOpen = ref<boolean>(false)
const rulesConsentModalOpen = ref<boolean>(false)
const rulesConsentLoading = ref<boolean>(false)
const pscErrorMessage = ref<string | undefined>(undefined)
const winCheckErrorMessage = ref<string | undefined>(undefined)
const winPickupErrorMessage = ref<string | undefined>(undefined)
const rulesConsentErrorMessage = ref<string | undefined>(undefined)

let countdownInterval:NodeJS.Timeout | undefined = undefined
let reloadInterval:NodeJS.Timeout | undefined = undefined

const showTodayWin = computed(() => {
  return userGameState.value !== UserGameState.NOGAME
})

const pscModalStep = ref<PscModalStep>(PscModalStep.FORM)
const openPscModal = () => {
  pscModalOpen.value = true
  pscModalStep.value = PscModalStep.FORM
}

const closePscModal = () => {
  pscModalOpen.value = false
}

const winningModalStep = ref<WinningModalStep>(WinningModalStep.FORM)

const openWinningModal = () => {
  winningsModalOpen.value = true
  winningModalStep.value = WinningModalStep.FORM
}

const closeWinningModal = () => {
  winningsModalOpen.value = false
}

const openRulesConsentModal = () => {
  rulesConsentModalOpen.value = true
  rulesConsentErrorMessage.value = undefined
}

const closeRulesConsentModal = () => {
  rulesConsentModalOpen.value = false
}

const confirmRulesConsent = async () => {
  rulesConsentLoading.value = true
  rulesConsentErrorMessage.value = undefined
  try {
    const userStore = useUserStore()
    await userStore.updateMe({
      hasRulesConsent: true,
      rulesConsentDatetime: new Date(),
    })
    rulesConsentModalOpen.value = false
    await doCheckWin()
  } catch (e: any) {
    logger.error(e)
    rulesConsentErrorMessage.value = t('modal.rulesConsent.error-save')
  } finally {
    rulesConsentLoading.value = false
  }
}

// User Game Detail
const userGameDetail = computed(() => {
  return userGamesDetailStore.userGameDetail
})

// Total prize
const activeDrawTotalPrize = computed(() => {
  if (!userGameDetail.value?.activeDraw) {
    return userGameDetail.value?.game?.prize
  }
  return (userGameDetail.value?.activeDraw?.prize || 0) + (userGameDetail.value?.activeDraw?.accumulatedPrize || 0)
})

// Next chance
const nextChance = computed(() => {
  return userGameDetail.value?.activeDraw?.nextDrawTimes?.drawTime
})

// PSC
const activeDrawWinningPsc = computed(() => {
  return userGameDetail.value?.activeDraw?.winningPsc
})

const submitPsc = async (payload: any) => {
  pscErrorMessage.value = undefined
  try {
    await userGamesDetailStore.registerToGame({
      game: userGameDetail.value?.game?.documentId,
      psc: payload.psc,
      hasAgeConsent: payload.ageConsent,
    })
    pscModalStep.value = PscModalStep.REGISTERED
  } catch (e: any) {
    logger.error(e)
    pscErrorMessage.value = t(e.error?.details?.key || 'modal.psc.error-register')
  }
}

const goBackWin = async () => {
  winningModalStep.value = WinningModalStep.FORM
}

const submitWin = async (payload: any) => {
  winPickupErrorMessage.value = undefined
  if (winningModalStep.value === WinningModalStep.FORM) {
    winningModalStep.value = WinningModalStep.REVIEW
    return
  }

  try {
    await userGamesDetailStore.pickupWin({
      game: userGameDetail.value?.game?.documentId,
      ...payload
    })
    winningModalStep.value = WinningModalStep.SAVED
    const gameDrawsStore = useGameDrawsStore('list')
    gameDrawsStore.fetchGameDraws(gameDrawsStore.filters)
    // TODO - re-fetch my last win and stats?
  } catch (e: any) {
    logger.error(e)
    winPickupErrorMessage.value = t(e.error?.details?.key || 'modal.winner.error-pickup')
  }
}

const doCheckWin = async () => {
  winCheckErrorMessage.value = undefined
  try {
    await userGamesDetailStore.checkWin({
      game: userGameDetail.value?.game?.documentId,
    })
  } catch (e: any) {
    logger.error(e)
    winCheckErrorMessage.value = t(e.error?.details?.key || 'raffle.error-pickup-win')
  }
}

const checkWin = async () => {

  // --- NAHRAZENO ROBUSTNÍ FUNKCÍ pushToDataLayer ---
  pushToDataLayer({
    "event": "clickTo_content",
    "clickTo": "pscVyhra",
    "link": {
      "text": "Zjistit, jestli je výhra moje"
    }
  });
  // -------------------------------------------------

  // Zkontroluj souhlas s pravidly – pokud ještě nebyl dán, zobraz modal
  if (!userGamesDetailStore.userGameDetail.user?.hasRulesConsent) {
    openRulesConsentModal()
    return
  }

  await doCheckWin()
}

const stopCountdownInterval = () => {
  clearInterval(countdownInterval);
  // release our intervalId from the variable
  countdownInterval = undefined;
}

const stopReloadInterval = () => {
  clearInterval(reloadInterval);
  // release our intervalId from the variable
  reloadInterval = undefined;
}

const drawDatetime = computed(() => {
  return userGamesDetailStore.userGameDetail?.activeDraw?.drawDatetime
})


// Fetch data
const fetchHeroData = async () => {
  try {
    await userGamesDetailStore.fetchUserGameDetail(config.public.homepageGameSlug)
    userGameState.value = userGameStateHelper.getUserGameState(userGamesDetailStore.userGameDetail)
    userGameStateCountdown.value = userGameStateHelper.getUserGameCountdown(userGamesDetailStore.userGameDetail)
  } catch (e: any) {
    logger.error(e)
  }
}

await fetchHeroData()

onMounted(() => {
  // check draw state and countdown
  stopCountdownInterval()
  countdownInterval = setInterval(() => {
    try {
      userGameState.value = userGameStateHelper.getUserGameState(userGamesDetailStore.userGameDetail)
      userGameStateCountdown.value = userGameStateHelper.getUserGameCountdown(userGamesDetailStore.userGameDetail)
    } catch (e: any) {
      logger.error(e)
    }
  }, 1000)

  // check NO GAME state - re-fetch data
  stopReloadInterval()
  reloadInterval = setInterval(() => {
    try {
      if (userGameState.value === UserGameState.NOGAME) {
        fetchHeroData()
      }
    } catch (e: any) {
      logger.error(e)
    }
  }, closedDrawReloadIntervalMs)
})

</script>
